import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Grid as MuiGrid,
  Paper as MuiPaper,
  Typography as MuiTypography,
  Divider as MuiDivider,
  TextField as MuiTextField,
  MenuItem as MuiMenuItem,
  Button as MuiButton,
  InputLabel as MuiInputLabel,
  Select as MuiSelect,
  FormControl as MuiFormControl,
  FormHelperText as MuiFormHelperText,
  Switch as MuiSwitch,
} from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format } from "date-fns";
import * as Yup from "yup";
import { useAlerts } from "common";
import { Formik, Form } from "formik";
import Mail from "app/shared/UI/Mail";
import { OptOutReason } from "app/shared/constants";
import {
  getProspectByMrn,
  editProspectById,
} from "app/services/prospectServices";
import { differenceObject } from "app/shared/utils";
import ConfirmationDialog from "app/shared/UI/Dialog/ConfirmationDialog";
import ProspectDetailsOutreachHistory from "app/components/prospect/ProspectDetailsOutreachHistory";
import ProspectDetailsCohortHistory from "app/components/prospect/ProspectDetailsCohortHistory";
import { editProspectPageStyles } from "./EditProspectPageStyles";
import { switchStyles } from "app/shared/UI/sharedStyles";

const EditProspectPageContent = () => {
  const {
    state: { prospect },
  } = useLocation();
  const { prospectId } = useParams();
  const { setAlert } = useAlerts();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [prospectData, setProspectData] = useState(prospect);
  const { emails = [], phones = [], addresses = [] } = prospectData;

  const [optedStatus, setOptedStatus] = useState(
    prospectData.optOut ? true : false
  );

  useEffect(() => {
    getProspectByMrn(prospectId, setProspectData, setAlert, setOptedStatus);
  }, [prospectId, setAlert]);

  const [openASConfirmation, setOpenASConfirmation] = useState(false);

  const formValidationSchema = Yup.object().shape({
    optOut: Yup.object()
      .shape({
        optOutReason: Yup.string()
          .required(t("formValidation.optOutReasonRequired"))
          .nullable(),
        optOutDate: Yup.date()
          .typeError(t("formValidation.invalidDateFormat"))
          .required(t("formValidation.optOutDateRequired"))
          .nullable(),
        optOutComment: Yup.string().required(
          t("formValidation.optOutCommentRequired")
        ),
      })
      .nullable()
      .default(undefined),
    concern: Yup.object()
      .shape({
        concernDate: Yup.date()
          .typeError(t("formValidation.invalidDateFormat"))
          .required(t("formValidation.concernDateReq"))
          .nullable(),
        concernDescription: Yup.string()
          .required(t("formValidation.concernReq"))
          .nullable(),
      })
      .nullable()
      .default(undefined),
  });
  const diff1 = (values) => differenceObject(values, prospectData);

  const handleClickCancel = (dirty) => {
    if (dirty) {
      setOpenASConfirmation(true);
    } else {
      navigate("/prospects/lookup");
    }
  };
  const closeCancelASConfirmation = (cancelChoice) => {
    setOpenASConfirmation(false);
    if (cancelChoice) {
      navigate("/prospects/lookup");
    }
  };

  const getValueSeparator = (value, type) => {
    if (type === "email") {
      return value.map((row, idx) => (
        <MuiTypography key={idx} variant="subtitle1">
          <Mail emailAddress={row}>{(idx ? ", " : "") + row}</Mail>
        </MuiTypography>
      ));
    }
    return (
      <MuiTypography variant="subtitle1">{value.join(", ")}</MuiTypography>
    );
  };

  return (
    <div sx={editProspectPageStyles.root}>
      <ConfirmationDialog
        open={openASConfirmation}
        onClose={closeCancelASConfirmation}
        message={t(`OutreachDetails.confirmationMessageCancel`)}
        okLabel={t("common.yesLabel")}
        cancelLabel={t("common.noLabel")}
      />
      <MuiGrid container spacing={3}>
        <MuiGrid size={6}>
          <MuiPaper sx={editProspectPageStyles.paper}>
            <MuiTypography>{t("LookupProspect.prospectDetails")}</MuiTypography>
            <MuiGrid
              container
              size={12}
              sx={editProspectPageStyles.customPadding}
            >
              <MuiGrid size={3}>{t("LookupProspect.tableColumnMRN")}:</MuiGrid>
              <MuiGrid size={9}>
                <MuiTypography variant="subtitle1">
                  {prospectData.mrn}
                </MuiTypography>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid
              container
              size={12}
              sx={editProspectPageStyles.customPadding}
            >
              <MuiGrid size={3}>
                {t("LookupProspect.tableColumnFirstName")}:
              </MuiGrid>
              <MuiGrid size={9}>
                <MuiTypography variant="subtitle1">
                  {prospectData.firstName}
                </MuiTypography>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid
              container
              size={12}
              sx={editProspectPageStyles.customPadding}
            >
              <MuiGrid size={3}>
                {t("LookupProspect.tableColumnLastName")}:
              </MuiGrid>
              <MuiGrid size={9}>
                <MuiTypography variant="subtitle1">
                  {prospectData.lastName}
                </MuiTypography>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid
              container
              size={12}
              sx={editProspectPageStyles.customPadding}
            >
              <MuiGrid size={3}>
                {t("LookupProspect.tableColumnEmail")}:
              </MuiGrid>
              <MuiGrid container size={9}>
                {getValueSeparator(emails, "email")}
              </MuiGrid>
            </MuiGrid>
            <MuiGrid
              container
              size={12}
              sx={editProspectPageStyles.customPadding}
            >
              <MuiGrid size={3}>
                {t("LookupProspect.tableColumnPhone")}:
              </MuiGrid>
              <MuiGrid container size={9}>
                {getValueSeparator(phones)}
              </MuiGrid>
            </MuiGrid>
            <MuiGrid
              container
              size={12}
              sx={editProspectPageStyles.customPadding}
            >
              <MuiGrid size={3}>{t("LookupProspect.tableColumnDOB")}:</MuiGrid>
              <MuiGrid size={9}>
                <MuiTypography variant="subtitle1">
                  {prospectData.dob}
                </MuiTypography>
              </MuiGrid>
            </MuiGrid>
            <MuiGrid
              container
              size={12}
              sx={editProspectPageStyles.customPadding}
            >
              <MuiGrid size={3}>
                {t("LookupProspect.tableColumnAddress")}:
              </MuiGrid>
              <MuiGrid
                size={9}
                sx={{
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {addresses.map((row, idx) => (
                  <MuiTypography key={idx} variant="subtitle1">
                    {row}
                    <MuiDivider orientation="horizontal" />
                  </MuiTypography>
                ))}
              </MuiGrid>
            </MuiGrid>
          </MuiPaper>
        </MuiGrid>
        <MuiGrid size={6}>
          <Formik
            initialValues={prospectData}
            enableReinitialize={true}
            validationSchema={formValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              editProspectById(
                prospectId,
                diff1(values),
                setAlert,
                setProspectData,
                navigate
              );
            }}
          >
            {({
              values,
              isValid,
              dirty,
              isSubmitting,
              handleBlur,
              setFieldValue,
              setValues,
              errors,
              touched,
            }) => {
              return (
                <Form>
                  <MuiPaper sx={editProspectPageStyles.paper}>
                    <MuiTypography>
                      {t("LookupProspect.editProspect")}
                    </MuiTypography>

                    <MuiGrid
                      container
                      size={12}
                      sx={editProspectPageStyles.customPadding}
                    >
                      <MuiGrid size={3}>
                        {t("LookupProspect.optedStatus")}
                      </MuiGrid>
                      <MuiTypography component="div">
                        <MuiGrid
                          component="label"
                          container
                          alignItems="center"
                          spacing={1}
                        >
                          <MuiGrid>
                            <MuiTypography variant="subtitle1">
                              In
                            </MuiTypography>
                          </MuiGrid>
                          <MuiGrid>
                            <MuiSwitch
                              sx={switchStyles}
                              name="optedStatus"
                              checked={optedStatus}
                              onChange={(evt) => {
                                setOptedStatus(evt.target.checked);
                                if (evt.target.checked) {
                                  setFieldValue(
                                    `optOut[optOutDate]`,
                                    new Date().toISOString()
                                  );
                                } else {
                                  setFieldValue(`optOut`, null);
                                }
                              }}
                            />
                          </MuiGrid>
                          <MuiGrid>
                            <MuiTypography variant="subtitle1">
                              Out
                            </MuiTypography>
                          </MuiGrid>
                        </MuiGrid>
                      </MuiTypography>
                    </MuiGrid>
                    <MuiGrid
                      size={12}
                      sx={editProspectPageStyles.customTopPadding}
                    >
                      <MuiFormControl
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        error={
                          errors.optOut &&
                          touched.optOutReason &&
                          Boolean(errors.optOut.optOutReason)
                        }
                        sx={editProspectPageStyles.selectMarginDense}
                      >
                        <MuiInputLabel
                          variant="outlined"
                          required={optedStatus}
                          disabled={!optedStatus}
                          id="optOutReason"
                          shrink
                        >
                          {t("LookupProspect.optOutReason")}
                        </MuiInputLabel>
                        <MuiSelect
                          label={t("LookupProspect.optOutReason")}
                          labelId="optOutReason"
                          size="small"
                          displayEmpty
                          onBlur={handleBlur}
                          value={
                            values.optOut && values.optOut.optOutReason
                              ? values.optOut.optOutReason
                              : ""
                          }
                          disabled={!optedStatus}
                          onChange={(evt) => {
                            setFieldValue(
                              `optOut[optOutReason]`,
                              evt.target.value
                            );
                          }}
                          fullWidth
                          name="optOutReason"
                          notched
                        >
                          <MuiMenuItem value={""}>
                            {t("formLabel.none")}
                          </MuiMenuItem>
                          {OptOutReason.map((item) => (
                            <MuiMenuItem key={item} value={item}>
                              {t("LookupProspect." + item)}
                            </MuiMenuItem>
                          ))}
                        </MuiSelect>
                        <MuiFormHelperText>
                          {errors &&
                            touched.optOutReason &&
                            errors.optOut?.optOutReason}
                        </MuiFormHelperText>
                      </MuiFormControl>
                    </MuiGrid>
                    <MuiGrid
                      container
                      sx={editProspectPageStyles.customTopPadding}
                      justifyContent="space-between"
                    >
                      <MuiGrid size={7}>
                        <MuiTextField
                          name="optOutComment"
                          label={t("LookupProspect.optOutComment")}
                          variant="outlined"
                          multiline={true}
                          onBlur={handleBlur}
                          disabled={!optedStatus}
                          fullWidth
                          size="small"
                          value={
                            values.optOut && values.optOut.optOutComment
                              ? values.optOut.optOutComment
                              : ""
                          }
                          onChange={(evt) => {
                            setFieldValue(
                              `optOut[optOutComment]`,
                              evt.target.value
                            );
                          }}
                          error={
                            errors.optOut &&
                            touched.optOutComment &&
                            Boolean(errors.optOut.optOutComment)
                          }
                          helperText={
                            errors &&
                            touched.optOutComment &&
                            errors.optOut?.optOutComment
                          }
                        />
                      </MuiGrid>
                      <MuiGrid size={4} sx={editProspectPageStyles.datePicker}>
                        <MuiLocalizationProvider
                          dateAdapter={MuiAdapterDateFns}
                        >
                          <MuiDatePicker
                            disableToolbar
                            variant="inline"
                            inputVariant="outlined"
                            label={t("LookupProspect.optOutDate")}
                            format="MM/dd/yyyy"
                            onBlur={handleBlur}
                            margin="dense"
                            disabled={!optedStatus}
                            value={
                              values.optOut && values.optOut.optOutDate
                                ? new Date(values.optOut.optOutDate)
                                : null
                            }
                            error={
                              errors.optOut && Boolean(errors.optOut.optOutDate)
                            }
                            helperText={errors && errors.optOut?.optOutDate}
                            onChange={(date, value) => {
                              const checkDate = Date.parse(date);

                              if (!isNaN(checkDate)) {
                                setFieldValue(
                                  `optOut[optOutDate]`,
                                  date.toISOString()
                                );
                              } else {
                                setFieldValue(`optOut[optOutDate]`, value);
                              }
                            }}
                            id="optOutDate"
                            name="optOutDate"
                            fullWidth
                            inputProps={{ autoComplete: "off" }}
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                size: "small",
                                InputLabelProps: { shrink: true },
                              },
                            }}
                          />
                        </MuiLocalizationProvider>
                      </MuiGrid>
                    </MuiGrid>
                    <MuiGrid
                      container
                      sx={editProspectPageStyles.customTopPadding}
                      justifyContent="space-between"
                    >
                      <MuiGrid size={7}>
                        <MuiTextField
                          name="concern"
                          label={t("LookupProspect.concern")}
                          variant="outlined"
                          multiline={true}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          value={
                            values.concern && values.concern.concernDescription
                          }
                          error={
                            errors.concern &&
                            touched.concernDescription &&
                            Boolean(errors.concern.concernDescription)
                          }
                          helperText={
                            errors &&
                            touched.concernDescription &&
                            errors.concern?.concernDescription
                          }
                          onChange={(evt) => {
                            const newData = evt.target.value
                              ? {
                                  ...values.concern,
                                  concernDescription: evt.target.value,
                                  concernDate: format(new Date(), "yyyy-MM-dd"),
                                }
                              : null;

                            setValues((prevValues) => ({
                              ...prevValues,
                              // we use the name to tell Formik which key of `values` to update
                              concern: newData,
                            }));
                          }}
                        />
                      </MuiGrid>
                      <MuiGrid size={4} sx={editProspectPageStyles.datePicker}>
                        <MuiLocalizationProvider
                          dateAdapter={MuiAdapterDateFns}
                        >
                          <MuiDatePicker
                            disableToolbar
                            variant="inline"
                            inputVariant="outlined"
                            label={t("LookupProspect.concernDate")}
                            format="MM/dd/yyyy"
                            onBlur={handleBlur}
                            margin="dense"
                            value={
                              values.concern && values.concern.concernDate
                                ? new Date(values.concern.concernDate)
                                : null
                            }
                            disabled={!values.concern}
                            error={
                              errors.concern &&
                              Boolean(errors.concern.concernDate)
                            }
                            helperText={errors && errors.concern?.concernDate}
                            onChange={(date, value) => {
                              const checkDate = Date.parse(date);

                              if (!isNaN(checkDate)) {
                                setFieldValue(
                                  `concern[concernDate]`,
                                  format(new Date(date), "yyyy-MM-dd")
                                );
                              } else {
                                setFieldValue(`concern[concernDate]`, value);
                              }
                            }}
                            id="concernDate"
                            name="concernDate"
                            fullWidth
                            inputProps={{ autoComplete: "off" }}
                            slotProps={{
                              textField: {
                                variant: "outlined",
                                size: "small",
                                InputLabelProps: { shrink: true },
                              },
                            }}
                          />
                        </MuiLocalizationProvider>
                      </MuiGrid>
                    </MuiGrid>
                    <MuiGrid
                      size={12}
                      sx={editProspectPageStyles.customTopPadding}
                    >
                      <MuiTextField
                        name="notes"
                        label={t("LookupProspect.notes")}
                        variant="outlined"
                        multiline={true}
                        onBlur={handleBlur}
                        fullWidth
                        size="small"
                        value={values.note}
                        onChange={(evt) => {
                          setFieldValue("note", evt.target.value);
                        }}
                      />
                    </MuiGrid>
                    <MuiGrid
                      container
                      justifyContent="flex-end"
                      sx={editProspectPageStyles.customPadding}
                    >
                      <MuiGrid container justifyContent="flex-end" size={2}>
                        <MuiButton
                          variant="outlined"
                          onClick={() => handleClickCancel(dirty)}
                        >
                          {t("formLabel.buttonCancel")}
                        </MuiButton>
                      </MuiGrid>
                      <MuiGrid container justifyContent="flex-end" size={2}>
                        <MuiButton
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={
                            !isValid ||
                            !dirty ||
                            isSubmitting ||
                            !Object.keys(diff1(values)).length > 0
                          }
                        >
                          {t("formLabel.submitButtonLabel")}
                        </MuiButton>
                      </MuiGrid>
                    </MuiGrid>
                  </MuiPaper>
                </Form>
              );
            }}
          </Formik>
        </MuiGrid>
        <MuiGrid size={12} sx={editProspectPageStyles.gridContainer}>
          <MuiTypography variant="h6">
            {t(`ProspectDetailsCohortHistory.cohortHistoryLabel`)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid size={12}>
          <ProspectDetailsCohortHistory mrn={prospectId} />
        </MuiGrid>
        <MuiGrid size={12} sx={editProspectPageStyles.gridContainer}>
          <MuiTypography variant="h6">
            {t(`ProspectDetailsOutreachHistory.outreachHistoryLabel`)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid size={12}>
          <ProspectDetailsOutreachHistory mrn={prospectId} />
        </MuiGrid>
      </MuiGrid>
    </div>
  );
};

export default EditProspectPageContent;
